import React, { useState, useEffect } from 'react';
import { doc, setDoc, getDoc, deleteDoc, collection, onSnapshot, query } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../lib/firebase';
import useAuthStore from '../store/useAuthStore';
import { DesktopService } from '../services/desktopService';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// ─── Icons ────────────────────────────────────
const LaptopIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25" /></svg>;
const TrashIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>;
const CheckIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>;
const EyeIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>;
const EyeOffIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" /></svg>;
const StarIcon = ({ className }) => <svg className={className} fill="currentColor" viewBox="0 0 24 24"><path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09l2.846.813-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" /></svg>;
const UserIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" /></svg>;
const CameraIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.04l-.821 1.316z" /><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" /></svg>;
const PlusIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>;
const PencilIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" /></svg>;
const XMarkIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>;
const SunIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 21v-2.25m-6.364-.386l1.591-1.591M3 12h2.25m.386-6.364l1.591 1.591M16.5 12a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" /></svg>;
const MoonIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" /></svg>;

export const TIMEZONES = [
    'Auto-detect', 'UTC', 'America/New_York', 'America/Chicago', 'America/Denver',
    'America/Los_Angeles', 'America/Toronto', 'Europe/London', 'Europe/Paris',
    'Europe/Berlin', 'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Kolkata', 'Asia/Dubai',
    'Australia/Sydney', 'Australia/Melbourne', 'Australia/Perth', 'Pacific/Auckland',
];

// ─── Provider Definitions ─────────────────────
const PROVIDERS = [
    {
        id: 'gemini', name: 'Google Gemini', icon: '✦',
        bgColor: 'bg-blue-500/10', textColor: 'text-blue-400',
        models: ['gemini-3.1-pro-preview', 'gemini-3.1-flash-lite-preview', 'gemini-3-flash-preview', 'gemini-3-pro-preview', 'gemini-2.5-pro', 'gemini-2.5-flash', 'gemini-2.5-flash-lite', 'gemini-2.0-flash'],
        keyPlaceholder: 'AIza...',
    },
    {
        id: 'anthropic', name: 'Anthropic', icon: '◈',
        bgColor: 'bg-orange-500/10', textColor: 'text-orange-400',
        models: ['claude-4-5-sonnet-latest', 'claude-4-5-opus-latest', 'claude-4-5-haiku-latest', 'claude-4-sonnet-latest', 'claude-3-7-sonnet-latest'],
        keyPlaceholder: 'sk-ant-api...',
    },
    {
        id: 'openai', name: 'OpenAI', icon: '◉',
        bgColor: 'bg-emerald-500/10', textColor: 'text-emerald-400',
        models: ['gpt-5.4-pro', 'gpt-5.4-thinking', 'gpt-5.2', 'gpt-4.5', 'gpt-4o', 'gpt-4o-mini', 'o4-mini', 'o3-mini'],
        keyPlaceholder: 'sk-...',
    },
    {
        id: 'ollama', name: 'Ollama (Local)', icon: '🦙',
        bgColor: 'bg-purple-500/10', textColor: 'text-purple-400',
        models: [], keyPlaceholder: null, isLocal: true,
    },
];

// ─── Sidebar Tab Definitions ──────────────────
const TABS = [
    { id: 'profile', label: 'Profile', icon: UserIcon },
    { id: 'devices', label: 'Connected Devices', icon: LaptopIcon },
    { id: 'providers', label: 'AI Providers', icon: StarIcon },
];

// ─── Device Card ──────────────────────────────
const DeviceCard = ({ device, onRemove }) => {
    const lastSeen = device.last_seen?.toDate?.();
    const [isOnline, setIsOnline] = useState(lastSeen && (new Date() - lastSeen) < 60000);
    const telemetry = device.telemetry;

    // Live ticker: re-evaluate "online" status every 10 seconds
    useEffect(() => {
        const checkStatus = () => {
            const currentLastSeen = device.last_seen?.toDate?.();
            if (currentLastSeen) {
                const diff = new Date() - currentLastSeen;
                setIsOnline(diff < 60000);
            } else {
                setIsOnline(false);
            }
        };

        const interval = setInterval(checkStatus, 10000);
        checkStatus(); // Initial check
        return () => clearInterval(interval);
    }, [device.last_seen]);

    return (
        <div className="flex items-center gap-4 py-4 border-b border-[var(--border-subtle)] group last:border-0 transition-all">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border transition-all duration-500 ${isOnline ? 'bg-green-500/5 border-green-500/20 text-green-500' : 'bg-[var(--bg-tertiary)]/30 border-[var(--border-subtle)] text-[var(--text-dim)]'}`}>
                <LaptopIcon className="w-5 h-5" />
            </div>

            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <h3 className="text-[15px] font-semibold text-[var(--text-main)] tracking-tight truncate">
                        {device.computerName || device.computer_name || 'Unknown Device'}
                    </h3>
                    <div className={`w-1.5 h-1.5 rounded-full ${isOnline ? 'bg-green-500 animate-pulse' : 'bg-[var(--text-dim)]'}`} />
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-[var(--text-muted)] font-medium">
                        {isOnline ? 'Active now' : lastSeen ? `Last seen ${lastSeen.toLocaleString()}` : 'Disconnected'}
                    </span>
                    {telemetry && isOnline && (
                        <div className="flex items-center gap-2 text-[10px] text-[var(--text-dim)] font-bold uppercase tracking-wider">
                            <span>•</span>
                            <span>CPU {telemetry.cpu_load}%</span>
                            <span>RAM {telemetry.ram_load}%</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex items-center gap-2">
                <button
                    onClick={() => onRemove(device.id)}
                    className="px-3 py-1.5 text-xs font-bold text-[var(--text-dim)] hover:text-red-400 hover:bg-red-400/5 rounded-lg border border-transparent hover:border-red-400/10 transition-all"
                >
                    Remove
                </button>
            </div>
        </div>
    );
};

// ─── AI Config List Item ────────────────────────
const AIConfigListItem = ({ configId, configData, isDefault, onEdit, onSetDefault, onDelete }) => {
    const providerDef = PROVIDERS.find(p => p.id === configData.provider) || PROVIDERS[0];
    const isConnected = providerDef.isLocal || !!configData.apiKey;

    return (
        <div className="flex items-center gap-4 py-4 border-b border-[var(--border-subtle)] group last:border-0 transition-all">
            <div className={`w-10 h-10 rounded-xl ${providerDef.bgColor} flex items-center justify-center text-lg font-bold shrink-0 border border-[var(--border-subtle)] shadow-sm`}>
                {providerDef.icon}
            </div>

            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <h4 className="text-[15px] font-semibold text-[var(--text-main)] tracking-tight truncate">
                        {configData.name || 'Untitled Config'}
                    </h4>
                    {isDefault && (
                        <span className="px-1.5 py-0.5 rounded bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] text-[9px] font-bold uppercase tracking-widest border border-[var(--accent-primary)]/20 shadow-sm">
                            Default
                        </span>
                    )}
                </div>
                <div className="text-xs text-[var(--text-muted)] font-medium mt-0.5">
                    {providerDef.name} • <span className="font-mono opacity-80">{configData.model || 'No model'}</span>
                </div>
            </div>

            <div className="flex items-center gap-2">
                {!isDefault && isConnected && (
                    <button
                        onClick={() => onSetDefault(configId)}
                        className="px-3 py-1.5 text-xs font-bold text-[var(--text-dim)] hover:text-[var(--accent-primary)] hover:bg-[var(--accent-primary)]/5 rounded-lg border border-[var(--border-subtle)] hover:border-[var(--accent-primary)]/20 transition-all"
                    >
                        Set Default
                    </button>
                )}
                <button
                    onClick={() => onEdit(configId)}
                    className="px-3 py-1.5 text-xs font-bold text-[var(--text-main)] bg-[var(--bg-tertiary)]/50 hover:bg-[var(--bg-tertiary)] rounded-lg border border-[var(--border-subtle)] transition-all"
                >
                    Configure
                </button>
                <button
                    onClick={() => onDelete(configId)}
                    className="p-1.5 text-[var(--text-dim)] hover:text-red-400 transition-colors"
                    title="Delete"
                >
                    <XMarkIcon className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

// ─── AI Config Edit Modal ───────────────────────
const AIConfigModal = ({ configId, configData, onSave, onClose, ollamaModels }) => {
    const [name, setName] = useState(configData.name || '');
    const [providerId, setProviderId] = useState(configData.provider || 'gemini');
    const [apiKey, setApiKey] = useState(configData.apiKey || '');
    const [selectedModel, setSelectedModel] = useState(configData.model || '');
    const [showKey, setShowKey] = useState(false);
    const [saving, setSaving] = useState(false);

    const providerDef = PROVIDERS.find(p => p.id === providerId) || PROVIDERS[0];
    const availableModels = providerDef.isLocal ? ollamaModels : providerDef.models;

    useEffect(() => {
        if (!selectedModel || !availableModels.includes(selectedModel)) {
            setSelectedModel(availableModels[0] || '');
        }
    }, [providerId, availableModels, selectedModel]);

    const handleSave = async () => {
        setSaving(true);
        await onSave(configId, {
            name: name.trim() || 'Untitled Config',
            provider: providerId,
            apiKey: providerDef.isLocal ? null : apiKey.trim(),
            model: selectedModel,
        });
        setSaving(false);
        onClose();
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6 overflow-hidden">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-black/60 backdrop-blur-md"
                    onClick={onClose}
                />

                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="relative bg-[var(--bg-main)] border border-[var(--border-subtle)] w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-full"
                >
                    <div className="px-8 py-6 border-b border-[var(--border-subtle)] flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-bold text-[var(--text-main)] tracking-tight">
                                {configData.provider ? 'Edit Provider' : 'New Provider'}
                            </h2>
                            <p className="text-xs text-[var(--text-muted)] mt-1">Configure your AI model and credentials.</p>
                        </div>
                        <button onClick={onClose} className="p-2 text-[var(--text-dim)] hover:text-[var(--text-main)] transition-colors">
                            <XMarkIcon className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="p-8 space-y-8 overflow-y-auto custom-scrollbar">
                        <section className="space-y-4">
                            <div className="space-y-1.5">
                                <label className="text-[11px] font-bold text-[var(--text-dim)] uppercase tracking-widest px-1">Friendly Name</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="e.g. Claude 3 Opus, GPT-4o..."
                                    className="w-full bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] text-[var(--text-main)] text-sm rounded-2xl px-5 py-3 outline-none focus:border-[var(--accent-primary)]/50 transition-all font-medium placeholder:opacity-30"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-[11px] font-bold text-[var(--text-dim)] uppercase tracking-widest px-1">Provider</label>
                                    <select
                                        value={providerId}
                                        onChange={(e) => setProviderId(e.target.value)}
                                        className="w-full bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] text-[var(--text-main)] text-sm rounded-2xl px-5 py-3 outline-none focus:border-[var(--accent-primary)]/50 transition-all font-medium appearance-none"
                                    >
                                        {PROVIDERS.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                    </select>
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-[11px] font-bold text-[var(--text-dim)] uppercase tracking-widest px-1">Model ID</label>
                                    <select
                                        value={selectedModel}
                                        onChange={(e) => setSelectedModel(e.target.value)}
                                        className="w-full bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] text-[var(--text-main)] text-sm rounded-2xl px-5 py-3 outline-none focus:border-[var(--accent-primary)]/50 transition-all font-medium appearance-none"
                                    >
                                        {availableModels.map(m => <option key={m} value={m}>{m}</option>)}
                                        {availableModels.length === 0 && <option value="">No models available</option>}
                                    </select>
                                </div>
                            </div>

                            {!providerDef.isLocal && (
                                <div className="space-y-1.5">
                                    <label className="text-[11px] font-bold text-[var(--text-dim)] uppercase tracking-widest px-1">API Credentials</label>
                                    <div className="relative">
                                        <input
                                            type={showKey ? 'text' : 'password'}
                                            value={apiKey}
                                            onChange={(e) => setApiKey(e.target.value)}
                                            placeholder={providerDef.keyPlaceholder}
                                            className="w-full bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] text-[var(--text-main)] text-sm rounded-2xl px-5 py-3 outline-none focus:border-[var(--accent-primary)]/50 transition-all font-mono placeholder:opacity-30"
                                        />
                                        <button
                                            onClick={() => setShowKey(!showKey)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-dim)] hover:text-[var(--text-main)] transition-colors"
                                            type="button"
                                        >
                                            {showKey ? <EyeOffIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                                        </button>
                                    </div>
                                    <p className="text-[10px] text-[var(--text-dim)] font-medium mt-1.5 ml-1">Stored securely in your encrypted personal preferences.</p>
                                </div>
                            )}

                            {providerDef.isLocal && (
                                <div className="bg-purple-500/5 border border-purple-500/10 rounded-2xl p-5 flex gap-5 transition-all duration-300">
                                    <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center shrink-0">
                                        <LaptopIcon className="w-5 h-5 text-purple-500" />
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-[13px] font-bold text-[var(--text-main)] tracking-tight">Local Connection Active</p>
                                        <p className="text-[11px] text-[var(--text-muted)] leading-relaxed font-medium">
                                            This provider connects directly to Ollama on your registered devices. No API key is required.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </section>
                    </div>

                    <div className="p-6 bg-[var(--bg-tertiary)]/30 border-t border-[var(--border-subtle)] flex justify-end gap-3">
                        <button
                            onClick={onClose}
                            className="px-6 py-2 text-sm font-bold text-[var(--text-dim)] hover:text-[var(--text-main)] transition-all"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={saving || (!providerDef.isLocal && !apiKey.trim())}
                            className="px-8 py-2 bg-[var(--text-main)] text-[var(--bg-main)] hover:opacity-90 disabled:opacity-30 text-sm font-bold rounded-xl transition-all shadow-lg"
                        >
                            {saving ? 'Saving...' : 'Save Configuration'}
                        </button>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

// ═══════════════════════════════════════════════
// MAIN SETTINGS PAGE
// ═══════════════════════════════════════════════

const SettingsPage = () => {
    const { user, profile: authProfile, logout, setTheme } = useAuthStore();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('profile');
    const [devices, setDevices] = useState([]);

    // AI Config store
    const [aiConfigs, setAiConfigs] = useState(null);
    const [editingConfigId, setEditingConfigId] = useState(null); // ID of config being edited
    const [ollamaModels, setOllamaModels] = useState([]);
    const [loadingDevices, setLoadingDevices] = useState(true);

    // Profile state
    const [profile, setProfile] = useState({
        displayName: '', bio: '', occupation: '', timezone: 'Auto-detect', avatarUrl: '',
    });
    const [profileSaving, setProfileSaving] = useState(false);
    const [profileSaved, setProfileSaved] = useState(false);
    const [avatarUploading, setAvatarUploading] = useState(false);

    const uid = user?.uid;

    // Listen to devices & update ollama models in real-time
    useEffect(() => {
        if (!uid) return;
        setLoadingDevices(true);

        const devicesRef = collection(db, 'users', uid, 'devices');
        const q = query(devicesRef);

        const unsub = onSnapshot(q, (snap) => {
            const devs = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setDevices(devs);

            // Re-discover models from any active device
            let modelsFound = [];
            for (const d of devs) {
                if (d.available_models?.length) {
                    modelsFound = d.available_models;
                    break;
                }
            }
            setOllamaModels(modelsFound);
            setLoadingDevices(false);
        }, (err) => {
            console.error("Error listening to devices:", err);
            setLoadingDevices(false);
        });

        return () => unsub();
    }, [uid]);

    // Listen to AI configurations in real-time
    useEffect(() => {
        if (!uid) return;
        const unsub = onSnapshot(doc(db, 'users', uid, 'settings', 'ai_configs'), (snap) => {
            if (snap.exists()) {
                setAiConfigs(snap.data());
            } else {
                setAiConfigs({ defaultConfigId: null, configs: {} });
            }
        }, (err) => {
            console.error("Error listening to AI configs:", err);
        });
        return () => unsub();
    }, [uid]);

    // Listen to profile in real-time
    useEffect(() => {
        if (!uid) return;
        const unsub = onSnapshot(doc(db, 'users', uid, 'settings', 'profile'), (snap) => {
            const settingsData = snap.exists() ? snap.data() : {};

            setProfile(prev => ({
                ...prev,
                displayName: settingsData.displayName || user?.displayName || '',
                bio: settingsData.bio || user?.bio || '',
                occupation: settingsData.occupation || user?.role || '',
                timezone: settingsData.timezone || user?.timezone || 'Auto-detect',
                avatarUrl: settingsData.avatarUrl || user?.avatarUrl || user?.photoURL || '',
            }));
        }, (err) => {
            console.error("Error listening to profile:", err);
        });
        return () => unsub();
    }, [uid, user, authProfile]);

    const handleProfileChange = (field, value) => {
        setProfile(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveProfile = async () => {
        setProfileSaving(true);
        try {
            await setDoc(doc(db, 'users', uid, 'settings', 'profile'), profile, { merge: true });
            await setDoc(doc(db, 'users', uid), {
                displayName: profile.displayName,
                avatarUrl: profile.avatarUrl,
            }, { merge: true });
            setProfileSaved(true);
            setTimeout(() => setProfileSaved(false), 2000);
        } catch (e) {
            console.error('Error saving profile:', e);
        } finally {
            setProfileSaving(false);
        }
    };

    const handleAvatarUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // 5MB limit
        if (file.size > 5_000_000) {
            alert('Image too large. Please use an image under 5MB.');
            return;
        }

        setAvatarUploading(true);
        try {
            // Upload to Firebase Storage
            const storageRef = ref(storage, `users/${uid}/profile_pictures/avatar_${Date.now()}`);
            await uploadBytes(storageRef, file);
            const downloadUrl = await getDownloadURL(storageRef);

            // Update UI
            setProfile(prev => ({ ...prev, avatarUrl: downloadUrl }));

            // Auto-save the new avatar URL to Firestore immediately
            await setDoc(doc(db, 'users', uid), { avatarUrl: downloadUrl }, { merge: true });
            await setDoc(doc(db, 'users', uid, 'settings', 'profile'), { avatarUrl: downloadUrl }, { merge: true });

        } catch (error) {
            console.error('Error uploading avatar:', error);
            alert('Failed to upload image. Please try again.');
        } finally {
            setAvatarUploading(false);
        }
    };

    // AI Config Management Actions
    const handleSaveConfig = async (configId, data) => {
        setAiConfigs(prev => {
            const updatedConfigs = {
                ...prev.configs,
                [configId]: { ...prev.configs[configId], ...data, id: configId }
            };
            const updated = { ...prev, configs: updatedConfigs };
            // If this is the first successful save, make it default
            if (Object.keys(updatedConfigs).length === 1 && !updated.defaultConfigId) {
                updated.defaultConfigId = configId;
            }

            // Sync to Firestore in the background
            setDoc(doc(db, 'users', uid, 'settings', 'ai_configs'), updated)
                .catch(err => console.error("Error saving config to Firestore:", err));

            return updated;
        });
    };

    const handleSetDefaultConfig = async (configId) => {
        setAiConfigs(prev => {
            const updated = { ...prev, defaultConfigId: configId };
            setDoc(doc(db, 'users', uid, 'settings', 'ai_configs'), updated)
                .catch(err => console.error("Error setting default config:", err));
            return updated;
        });
    };

    const handleDeleteConfig = async (configId) => {
        if (!window.confirm('Delete this AI configuration?')) return;

        setAiConfigs(prev => {
            const updatedConfigs = { ...prev.configs };
            delete updatedConfigs[configId];

            let newDefault = prev.defaultConfigId;
            if (newDefault === configId) {
                const keys = Object.keys(updatedConfigs);
                newDefault = keys.length > 0 ? keys[0] : null;
            }

            const updated = { ...prev, configs: updatedConfigs, defaultConfigId: newDefault };
            setDoc(doc(db, 'users', uid, 'settings', 'ai_configs'), updated)
                .catch(err => console.error("Error deleting config:", err));
            return updated;
        });
    };

    const handleAddConfig = () => {
        const newId = Date.now().toString();
        setAiConfigs(prev => ({
            ...prev,
            configs: {
                ...prev.configs,
                [newId]: { name: '', provider: 'gemini', model: 'gemini-2.5-flash', id: newId }
            }
        }));
        setEditingConfigId(newId); // Open modal immediately
    };

    const handleEditConfig = (configId) => {
        setEditingConfigId(configId);
    };

    const handleCloseModal = () => {
        const idToRemove = editingConfigId;
        setEditingConfigId(null);

        // Functional cleanup to avoid stale state in handleCloseModal
        setAiConfigs(prev => {
            if (!prev || !idToRemove) return prev;
            const activeConfig = prev.configs[idToRemove];
            // If it's a new unsaved config (no name AND no key), remove it silently
            if (activeConfig && !activeConfig.name && !activeConfig.apiKey) {
                const updatedConfigs = { ...prev.configs };
                delete updatedConfigs[idToRemove];
                return { ...prev, configs: updatedConfigs };
            }
            return prev;
        });
    };

    const handleRemoveDevice = async (deviceId) => {
        if (!window.confirm('Remove this device? It will need to be re-registered.')) return;
        try {
            await deleteDoc(doc(db, 'users', uid, 'devices', deviceId));
            setDevices(prev => prev.filter(d => d.id !== deviceId));
        } catch (e) {
            console.error('Error removing device:', e);
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    if (!user) return null;

    // ─── Content Panels ───────────────────────
    const renderContent = () => {
        switch (activeTab) {
            case 'profile':
                return (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-12"
                    >
                        <section>
                            <h2 className="text-3xl md:text-4xl font-light text-[var(--text-main)] mb-1.5 tracking-tighter transition-all">Profile</h2>
                            <p className="text-sm text-[var(--text-muted)] leading-relaxed max-w-xl">
                                Manage your personal information. This context is shared with your AI assistants to provide more personalized and relevant responses.
                            </p>
                        </section>

                        <section className="space-y-8">
                            <div className="flex items-center gap-6">
                                <div className="relative group">
                                    <div className="w-24 h-24 rounded-full bg-[var(--bg-tertiary)]/30 border border-[var(--border-subtle)] overflow-hidden shrink-0 shadow-lg backdrop-blur-sm">
                                        {profile.avatarUrl ? (
                                            <img src={profile.avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-3xl font-light text-[var(--text-muted)] bg-[var(--bg-tertiary)]/20 uppercase">
                                                {(profile.displayName || user.displayName)?.charAt(0) || '?'}
                                            </div>
                                        )}
                                    </div>
                                    <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition-all backdrop-blur-[2px]">
                                        {avatarUploading ? (
                                            <svg className="animate-spin w-6 h-6 text-white" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                                        ) : (
                                            <CameraIcon className="w-6 h-6 text-white" />
                                        )}
                                        <input type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" />
                                    </label>
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-lg font-semibold text-[var(--text-main)] truncate">{profile.displayName || "Anonymous User"}</h3>
                                    <p className="text-sm text-[var(--text-muted)] font-medium tracking-tight opacity-60">{user.email}</p>
                                    <p className="text-[11px] text-[var(--text-dim)] font-bold uppercase tracking-widest mt-2">Recommended: 400x400px</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-1.5">
                                    <label className="text-[11px] font-bold text-[var(--text-dim)] uppercase tracking-widest px-1">Display Name</label>
                                    <input
                                        type="text"
                                        value={profile.displayName}
                                        onChange={(e) => handleProfileChange('displayName', e.target.value)}
                                        className="w-full bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] text-[var(--text-main)] text-sm rounded-2xl px-5 py-3 outline-none focus:border-[var(--accent-primary)]/50 transition-all font-medium placeholder:opacity-30"
                                        placeholder="Enter your name"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[11px] font-bold text-[var(--text-dim)] uppercase tracking-widest px-1">Occupation</label>
                                    <input
                                        type="text"
                                        value={profile.occupation}
                                        onChange={(e) => handleProfileChange('occupation', e.target.value)}
                                        className="w-full bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] text-[var(--text-main)] text-sm rounded-2xl px-5 py-3 outline-none focus:border-[var(--accent-primary)]/50 transition-all font-medium placeholder:opacity-30"
                                        placeholder="e.g. Lead Engineer"
                                    />
                                </div>
                                <div className="md:col-span-2 space-y-1.5">
                                    <label className="text-[11px] font-bold text-[var(--text-dim)] uppercase tracking-widest px-1">About You</label>
                                    <textarea
                                        value={profile.bio}
                                        onChange={(e) => handleProfileChange('bio', e.target.value)}
                                        rows={4}
                                        className="w-full bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] text-[var(--text-main)] text-sm rounded-2xl px-5 py-3.5 outline-none focus:border-[var(--accent-primary)]/50 transition-all font-medium resize-none leading-relaxed placeholder:opacity-30"
                                        placeholder="Tell your AI assistant how you like to work, your technical background, or any preferred communication styles..."
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[11px] font-bold text-[var(--text-dim)] uppercase tracking-widest px-1">Timezone</label>
                                    <select
                                        value={profile.timezone}
                                        onChange={(e) => handleProfileChange('timezone', e.target.value)}
                                        className="w-full bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] text-[var(--text-main)] text-sm rounded-2xl px-5 py-3 outline-none focus:border-[var(--accent-primary)]/50 transition-all font-medium appearance-none"
                                    >
                                        {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="pt-8 border-t border-[var(--border-subtle)] flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <p className="text-xs text-[var(--text-muted)] max-w-sm">
                                    Your data is stored securely and only used to improve your core agent interactions.
                                </p>
                                <button
                                    onClick={handleSaveProfile}
                                    disabled={profileSaving}
                                    className="px-8 py-2.5 bg-[var(--text-main)] text-[var(--bg-main)] hover:opacity-90 disabled:opacity-30 disabled:hover:opacity-30 text-sm font-bold rounded-full transition-all flex items-center justify-center gap-2 shadow-lg"
                                >
                                    {profileSaving ? (
                                        <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                                    ) : profileSaved ? <CheckIcon className="w-4 h-4" /> : null}
                                    {profileSaving ? 'Saving...' : profileSaved ? 'Saved' : 'Update Profile'}
                                </button>
                            </div>
                        </section>
                    </motion.div>
                );

            case 'devices':
                return (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-12"
                    >
                        <section>
                            <h2 className="text-3xl md:text-4xl font-light text-[var(--text-main)] mb-1.5 tracking-tighter transition-all">Devices</h2>
                            <p className="text-sm text-[var(--text-muted)] leading-relaxed max-w-xl">
                                Manage the desktop instances connected to your account. Connected devices enable powers like screen vision and remote file orchestration.
                            </p>
                        </section>

                        <section>
                            <div className="flex flex-col">
                                {loadingDevices ? (
                                    <div className="flex items-center justify-center py-12">
                                        <svg className="animate-spin h-6 w-6 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                                    </div>
                                ) : devices.length === 0 ? (
                                    <div className="py-20 flex flex-col items-center text-center">
                                        <div className="w-16 h-16 rounded-2xl bg-[var(--bg-tertiary)]/30 border border-[var(--border-subtle)] flex items-center justify-center mb-4 text-[var(--text-dim)]">
                                            <LaptopIcon className="w-8 h-8" />
                                        </div>
                                        <p className="text-sm font-semibold text-[var(--text-main)] mb-1">No devices found</p>
                                        <p className="text-xs text-[var(--text-muted)] max-w-[200px]">Install the desktop agent to register your first device.</p>
                                    </div>
                                ) : (
                                    devices.map(device => (
                                        <DeviceCard key={device.id} device={device} onRemove={handleRemoveDevice} />
                                    ))
                                )}
                            </div>
                        </section>
                    </motion.div>
                );

            case 'providers':
                return (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-12"
                    >
                        <section className="mb-12">
                            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                                <div>
                                    <h2 className="text-3xl md:text-4xl font-light text-[var(--text-main)] mb-1.5 tracking-tighter transition-all">AI Providers</h2>
                                    <p className="text-sm text-[var(--text-muted)] leading-relaxed max-w-xl">
                                        Configure external and local AI models to power your intelligent agent interactions.
                                    </p>
                                </div>
                                <button
                                    onClick={handleAddConfig}
                                    className="flex items-center justify-center gap-2 px-6 py-2.5 bg-[var(--text-main)] text-[var(--bg-main)] hover:opacity-90 transition-all text-sm font-bold rounded-full shadow-lg h-fit w-full md:w-auto"
                                >
                                    <PlusIcon className="w-4 h-4" />
                                    Add Provider
                                </button>
                            </div>
                        </section>

                        <section>
                            <div className="flex flex-col">
                                {aiConfigs === null ? (
                                    <div className="flex items-center justify-center py-12">
                                        <svg className="animate-spin h-6 w-6 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                                    </div>
                                ) : Object.keys(aiConfigs.configs).length === 0 ? (
                                    <div className="py-20 flex flex-col items-center text-center">
                                        <div className="w-16 h-16 rounded-2xl bg-[var(--bg-tertiary)]/30 border border-[var(--border-subtle)] flex items-center justify-center mb-4 text-[var(--text-dim)]">
                                            <StarIcon className="w-8 h-8" />
                                        </div>
                                        <p className="text-sm font-semibold text-[var(--text-main)] mb-1">No providers configured</p>
                                        <p className="text-xs text-[var(--text-muted)] max-w-[200px]">Add your API keys to enable foundation model access.</p>
                                    </div>
                                ) : (
                                    Object.entries(aiConfigs.configs).map(([id, config]) => (
                                        <AIConfigListItem
                                            key={id}
                                            configId={id}
                                            configData={config}
                                            isDefault={aiConfigs.defaultConfigId === id}
                                            onEdit={handleEditConfig}
                                            onSetDefault={handleSetDefaultConfig}
                                            onDelete={handleDeleteConfig}
                                        />
                                    ))
                                )}
                            </div>
                        </section>
                    </motion.div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-[var(--bg-main)] text-[var(--text-main)] font-sans transition-colors duration-300">
            <div className="max-w-7xl mx-auto px-6 py-12 md:py-20 lg:py-24 flex flex-col md:flex-row gap-16 md:gap-24 relative justify-center">
                {/* ─── Sidebar ─────────────────────────── */}
                <aside className="w-full md:w-56 shrink-0 md:sticky md:top-24 h-auto md:h-fit flex flex-col">
                    <div className="mb-12 px-2">
                        <h1 className="text-3xl md:text-4xl font-light text-[var(--text-main)] tracking-tighter transition-all">
                            Settings
                        </h1>
                    </div>

                    <nav className="space-y-1">
                        {TABS.map(tab => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all text-left group ${isActive
                                        ? 'bg-[var(--bg-tertiary)]/50 text-[var(--text-main)]'
                                        : 'text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-tertiary)]/30'
                                        }`}
                                >
                                    <Icon className={`w-4 h-4 shrink-0 transition-colors ${isActive ? 'text-[var(--accent-primary)]' : 'text-[var(--text-dim)] group-hover:text-[var(--text-muted)]'}`} />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </nav>

                    <div className="mt-12 hidden md:block border-t border-[var(--border-subtle)] pt-4">
                        <button
                            onClick={handleLogout}
                            className="w-full px-3 py-2 text-[10px] font-bold text-[var(--text-dim)] hover:text-red-400 text-left transition-colors uppercase tracking-widest"
                        >
                            Sign Out
                        </button>
                    </div>
                </aside>

                {/* ─── Main Content ────────────────────── */}
                <main className="flex-1 min-w-0 max-w-3xl">
                    <div className="md:px-4">
                        {renderContent()}
                    </div>

                    {editingConfigId && aiConfigs?.configs[editingConfigId] && (
                        <AIConfigModal
                            configId={editingConfigId}
                            configData={aiConfigs.configs[editingConfigId]}
                            onSave={handleSaveConfig}
                            onClose={handleCloseModal}
                            ollamaModels={ollamaModels}
                        />
                    )}
                </main>
            </div>
        </div>
    );
};

export default SettingsPage;
